import type { CategoryDto, ProductDto } from '@deli/contracts';
import { Fragment, useState, type FormEvent } from 'react';
import {
  PRODUCT_PAGE,
  useCategories,
  useCountStock,
  useProducts,
  useUpdateProduct,
  type ProductFilter,
} from '../api/catalogue.js';
import { Can, hasPermission, useCurrentUser } from '../auth/session.js';
import { ErrorText } from '../components/ErrorText.js';
import { Pager } from '../components/Pager.js';
import { QueryState } from '../components/QueryState.js';
import { formatMoney, parseMoneyInput, toMoneyInput } from '../format.js';
import { t } from '../i18n/uk.js';
import { CategoryTree, ImportPanel, NewProductButton } from './CatalogueAdmin.js';
import { ProductImages } from './ProductImages.js';

const tc = t.catalogue;

/** Prices, active flag and stock count of one product; every change needs a reason. */
function ProductEditor({
  product,
  categories,
}: {
  product: ProductDto;
  categories: CategoryDto[];
}) {
  const update = useUpdateProduct();
  const count = useCountStock();
  const [prices, setPrices] = useState({
    name: product.name,
    categoryId: product.categoryId ?? '',
    basePrice: toMoneyInput(product.basePrice),
    costPrice: toMoneyInput(product.costPrice),
    isActive: product.isActive,
    reason: '',
  });
  const [stock, setStock] = useState({ onHand: String(product.stock.onHand), reason: '' });
  const [invalid, setInvalid] = useState(false);

  const savePrices = (event: FormEvent) => {
    event.preventDefault();
    const basePrice = parseMoneyInput(prices.basePrice);
    const costPrice = prices.costPrice.trim() === '' ? null : parseMoneyInput(prices.costPrice);
    if (basePrice === null || (prices.costPrice.trim() !== '' && costPrice === null)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    update.mutate(
      {
        id: product.id,
        name: prices.name,
        categoryId: prices.categoryId || null,
        basePrice,
        costPrice,
        isActive: prices.isActive,
        reason: prices.reason,
      },
      { onSuccess: () => setPrices((p) => ({ ...p, reason: '' })) },
    );
  };

  const saveCount = (event: FormEvent) => {
    event.preventDefault();
    count.mutate(
      { id: product.id, onHand: Number(stock.onHand), reason: stock.reason },
      { onSuccess: () => setStock((s) => ({ ...s, reason: '' })) },
    );
  };

  const fromStore = product.wooProductId !== null;
  return (
    <div className="editor">
      <form className="inline-form" onSubmit={savePrices}>
        <h2>{tc.editTitle}</h2>
        {fromStore && <p className="muted hint">{tc.fromStoreHint}</p>}
        <label>
          {tc.name}
          <input
            aria-label={`${tc.name}: ${product.sku}`}
            required
            readOnly={fromStore}
            value={prices.name}
            onChange={(e) => setPrices({ ...prices, name: e.target.value })}
          />
        </label>
        <label>
          {tc.category}
          <select
            aria-label={`${tc.category}: ${product.sku}`}
            disabled={fromStore}
            value={prices.categoryId}
            onChange={(e) => setPrices({ ...prices, categoryId: e.target.value })}
          >
            <option value="">{tc.noCategory}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.path}
              </option>
            ))}
          </select>
        </label>
        <label>
          {tc.basePrice}, ₴
          <input
            aria-label={tc.basePrice}
            inputMode="decimal"
            required
            value={prices.basePrice}
            onChange={(e) => setPrices({ ...prices, basePrice: e.target.value })}
          />
        </label>
        <label>
          {tc.costPrice}, ₴
          <input
            aria-label={tc.costPrice}
            inputMode="decimal"
            value={prices.costPrice}
            onChange={(e) => setPrices({ ...prices, costPrice: e.target.value })}
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={prices.isActive}
            onChange={(e) => setPrices({ ...prices, isActive: e.target.checked })}
          />
          {tc.active}
        </label>
        <input
          aria-label={`${t.common.reason}: ${tc.editTitle}`}
          placeholder={t.common.reason}
          required
          value={prices.reason}
          onChange={(e) => setPrices({ ...prices, reason: e.target.value })}
        />
        <button type="submit" className="primary" disabled={update.isPending}>
          {t.common.save}
        </button>
        {update.isSuccess && <span className="ok-text">{t.common.saved}</span>}
        {invalid && <p className="error-text">{t.common.invalidAmount}</p>}
        <ErrorText error={update.error} />
      </form>
      <form className="inline-form" onSubmit={saveCount}>
        <h2>{tc.countTitle}</h2>
        <p className="muted hint">{tc.countHint}</p>
        <label>
          {tc.onHand}
          <input
            aria-label={tc.onHand}
            type="number"
            min={0}
            step={1}
            required
            value={stock.onHand}
            onChange={(e) => setStock({ ...stock, onHand: e.target.value })}
          />
        </label>
        <input
          aria-label={`${t.common.reason}: ${tc.countTitle}`}
          placeholder={t.common.reason}
          required
          value={stock.reason}
          onChange={(e) => setStock({ ...stock, reason: e.target.value })}
        />
        <button type="submit" className="primary" disabled={count.isPending}>
          {tc.saveCount}
        </button>
        {count.isSuccess && <span className="ok-text">{t.common.saved}</span>}
        <ErrorText error={count.error} />
      </form>
    </div>
  );
}

export function CataloguePage() {
  const me = useCurrentUser();
  const canEdit = hasPermission(me, 'catalogue.edit');
  const [draft, setDraft] = useState<ProductFilter>({ active: true });
  const [filter, setFilter] = useState<ProductFilter>({ active: true });
  const [open, setOpen] = useState<string | null>(null);
  const products = useProducts(filter);
  const categories = useCategories();

  const apply = (event: FormEvent) => {
    event.preventDefault();
    setFilter({ ...draft, search: draft.search?.trim() || undefined, offset: 0 });
  };

  return (
    <section className="wide">
      <div className="page-header">
        <h1>{tc.title}</h1>
        <Can permission="catalogue.manage">
          <NewProductButton />
        </Can>
      </div>
      <Can permission="catalogue.manage">
        <CategoryTree />
      </Can>
      <Can permission="catalogue.import">
        <ImportPanel />
      </Can>
      <form className="card inline-form" onSubmit={apply}>
        <input
          aria-label={t.common.search}
          placeholder={tc.searchPlaceholder}
          value={draft.search ?? ''}
          onChange={(e) => setDraft({ ...draft, search: e.target.value })}
        />
        <select
          aria-label={tc.category}
          value={draft.categoryId ?? ''}
          onChange={(e) => setDraft({ ...draft, categoryId: e.target.value || undefined })}
        >
          <option value="">{tc.allCategories}</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.path}
            </option>
          ))}
        </select>
        <label className="check">
          <input
            type="checkbox"
            checked={draft.active === true}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked ? true : undefined })}
          />
          {tc.onlyActive}
        </label>
        <button type="submit">{t.common.search}</button>
      </form>
      <QueryState query={products}>
        {(page) =>
          page.items.length === 0 ? (
            <p className="muted">{tc.empty}</p>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th aria-label={tc.heroImage} />
                    <th>{tc.sku}</th>
                    <th>{tc.name}</th>
                    <th>{tc.category}</th>
                    <th className="num">{tc.basePrice}</th>
                    <th className="num">{tc.costPrice}</th>
                    <th className="num">{tc.available}</th>
                    <th className="num">
                      {tc.onHand} / {tc.reserved}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((p) => (
                    <Fragment key={p.id}>
                      <tr>
                        <td className="thumb-cell">
                          {p.heroImage ? (
                            <img src={p.heroImage.url} alt="" className="thumb" loading="lazy" />
                          ) : p.wooImages[0] ? (
                            <img
                              src={p.wooImages[0]}
                              alt=""
                              className="thumb"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span className="thumb placeholder" />
                          )}
                        </td>
                        <td>
                          {canEdit ? (
                            <button
                              type="button"
                              className="link"
                              aria-expanded={open === p.id}
                              onClick={() => setOpen(open === p.id ? null : p.id)}
                            >
                              {p.sku}
                            </button>
                          ) : (
                            <code>{p.sku}</code>
                          )}
                        </td>
                        <td>
                          {p.name}
                          {!p.isActive && <span className="badge bad">{tc.inactive}</span>}
                          {p.wooProductId !== null && <span className="badge">Woo</span>}
                        </td>
                        <td className="muted">{p.categoryPath ?? '—'}</td>
                        <td className="num">{formatMoney(p.basePrice)}</td>
                        <td className="num">{formatMoney(p.costPrice)}</td>
                        <td className={p.stock.available > 0 ? 'num' : 'num bad-text'}>
                          {p.stock.available}
                        </td>
                        <td className="num muted">
                          {p.stock.onHand} / {p.stock.reserved}
                        </td>
                      </tr>
                      {open === p.id && (
                        <tr>
                          <td colSpan={8}>
                            <ProductEditor product={p} categories={categories.data ?? []} />
                            <ProductImages product={p} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              <Pager
                offset={filter.offset ?? 0}
                pageSize={PRODUCT_PAGE}
                total={page.total}
                onChange={(offset) => setFilter({ ...filter, offset })}
              />
            </>
          )
        }
      </QueryState>
    </section>
  );
}
